import { useRef, useState } from 'react';
import axios from 'axios';
import { uploadFile } from '../../helper/firebase';
import { triggerConfetti } from './confetti';

const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

// Resolves once the browser has the photo's bytes. A photo picked from the
// phone's library can take a while to arrive (e.g. downloading from iCloud).
const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve();
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

/**
 * State and behaviour for one kind of photo-backed shift action (check-in or
 * check-out) across every set in the Election Day widget: photo selection and
 * preview, the location permission flow with its "still proceed" fallback, and
 * upload → save → success. The action button stays disabled until the browser
 * has finished loading the chosen photo (isPhotoReady), so a DJ can't check in
 * while a photo from the library is still arriving. Nothing is uploaded until
 * the DJ taps the button. Instantiate it once per kind; every piece of state
 * is keyed by set id inside the instance, so the two kinds never collide.
 *
 * The caller owns the assignment records, the shared location modal, and the
 * shared in-flight map:
 *  - kind names this instance ('checkin' | 'checkout').
 *  - inFlight / setInFlight is a { [setId]: kind } map shared by both
 *    instances. While a set has any action in flight, neither instance will
 *    start another for that set: each save spreads the caller's current copy
 *    of the record, so two overlapping saves would race and the later one
 *    would overwrite the earlier one's fields in Quickbase.
 *  - getAssignment(setId) returns the current full record. It is spread into
 *    the save payload because Quickbase blanks any writable field it is not
 *    sent, so the whole record must always travel with the changes.
 *  - buildChanges({ imageUrl, coords }) returns the fields this action writes.
 *  - onSaved(setId, changes) lets the caller fold the written fields back into
 *    its records so a later action in the same session sends them forward.
 *  - requestLocationPrompt(setId) asks the caller to open the modal.
 *  - uploadFolder is the election's Firebase Storage folder for photos, as
 *    Quickbase holds it; uploadFile normalizes it and handles blank.
 *  - labels: { verb: 'check in', gerund: 'checking in', noun: 'Check-in' }.
 */
export default function useShiftPhotoAction({
  kind,
  labels,
  inFlight,
  setInFlight,
  getAssignment,
  buildChanges,
  onSaved,
  requestLocationPrompt,
  uploadFolder,
}) {
  const [success, setSuccess] = useState({});
  const [images, setImages] = useState({});
  const [imageError, setImageError] = useState({});
  const [locationError, setLocationError] = useState({});
  const [locationData, setLocationData] = useState({});
  const [skipLocation, setSkipLocation] = useState({});
  const fileInputRefs = useRef({});
  // The latest photo chosen for each set. A load that finishes after its
  // photo was removed or replaced is ignored.
  const loadTokens = useRef({});
  // Synchronous guards against a double tap. inFlight is React state, so two
  // taps in the same frame both see it empty; these refs are set immediately.
  const requesting = useRef({});
  const running = useRef({});

  const patch = (setter, setId, value) => setter((prev) => ({ ...prev, [setId]: value }));

  // This instance's own action is in flight for the set.
  const isLoading = (setId) => inFlight[setId] === kind;
  // Any action, of either kind, is in flight for the set.
  const isBusy = (setId) => Boolean(inFlight[setId]);
  // The chosen photo has finished loading and can be uploaded.
  const isPhotoReady = (setId) => Boolean(images[setId] && images[setId].status === 'ready');
  // A photo is chosen and still loading.
  const isPhotoLoading = (setId) => Boolean(images[setId] && images[setId].status === 'loading');
  const start = (setId) => {
    running.current[setId] = true;
    patch(setInFlight, setId, kind);
  };
  const finish = (setId) => {
    running.current[setId] = false;
    setInFlight((prev) => {
      const next = { ...prev };
      delete next[setId];
      return next;
    });
  };

  const registerFileInput = (setId, inputType) => (el) => {
    if (!fileInputRefs.current[setId]) {
      fileInputRefs.current[setId] = {};
    }
    fileInputRefs.current[setId][inputType] = el;
  };

  const triggerFileInput = (setId, inputType) => {
    const input = fileInputRefs.current[setId] && fileInputRefs.current[setId][inputType];
    if (input) input.click();
  };

  const handleFileSelect = (setId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      patch(setImageError, setId, 'Please select an image file (JPEG, PNG).');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      patch(setImageError, setId, 'Image must be less than 15MB.');
      return;
    }

    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);
    patch(setSkipLocation, setId, false);

    const token = {};
    loadTokens.current[setId] = token;
    const url = URL.createObjectURL(file);
    patch(setImages, setId, { file, url, status: 'loading' });

    readFile(file)
      .then(() => {
        if (loadTokens.current[setId] !== token) return;
        patch(setImages, setId, { file, url, status: 'ready' });
      })
      .catch((error) => {
        if (loadTokens.current[setId] !== token) return;
        console.error('Error reading image:', error);
        patch(setImages, setId, { file, url, status: 'failed' });
        patch(setImageError, setId, "We couldn't load this photo. Remove it and choose it again.");
      });
  };

  const removeImage = (setId) => {
    delete loadTokens.current[setId];
    if (images[setId] && images[setId].url) {
      URL.revokeObjectURL(images[setId].url);
    }
    setImages((prev) => {
      const next = { ...prev };
      delete next[setId];
      return next;
    });
    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);
  };

  // Fetch the position (unless told to skip it), then upload and save.
  const proceed = (setId, skipLocationCheck = false) => {
    if (isBusy(setId) || running.current[setId]) return;
    if (!isPhotoReady(setId)) {
      patch(setImageError, setId, `Please wait for your photo to finish loading before ${labels.gerund}.`);
      return;
    }

    if (!skipLocationCheck && !navigator.geolocation) {
      patch(
        setLocationError,
        setId,
        `Geolocation is not supported by your browser or you have sharing blocked. You can still ${labels.verb} without sharing location.`
      );
      patch(setSkipLocation, setId, true);
      return;
    }

    start(setId);

    const save = async (coords = null) => {
      try {
        const assignment = getAssignment(setId);
        if (!assignment) {
          throw new Error('Set not found');
        }

        let imageUrl = '';
        try {
          const uploadResult = await uploadFile(images[setId].file, setId, uploadFolder);
          imageUrl = uploadResult.url;
        } catch (error) {
          console.error('Error uploading image:', error);
          throw new Error('Failed to upload image. Please try again.');
        }

        const changes = buildChanges({ imageUrl, coords });
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveAssignment`, {
          assignments: [{ ...assignment, ...changes }],
        });

        if (!response.data.success) {
          throw new Error(`${labels.noun} failed`);
        }

        onSaved(setId, changes);
        patch(setSuccess, setId, true);
        triggerConfetti();

        if (images[setId] && images[setId].url) {
          URL.revokeObjectURL(images[setId].url);
        }
      } catch (error) {
        console.error(`Error during ${labels.verb}:`, error);
        patch(setImageError, setId, `${labels.noun} failed: ${error.message || 'Unknown error'}`);
      } finally {
        finish(setId);
      }
    };

    if (skipLocationCheck) {
      save();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        patch(setLocationData, setId, coords);
        await save(coords);
      },
      (error) => {
        console.error('Geolocation error:', error);
        patch(setLocationError, setId, `Location access failed, but you can still ${labels.verb}.`);
        patch(setSkipLocation, setId, true);
        // Nothing is in flight any more; leave the action button usable.
        finish(setId);
      }
    );
  };

  // Entry point for the action button: require a photo, then route on the
  // browser's location permission state.
  const request = (setId) => {
    if (isBusy(setId) || running.current[setId] || requesting.current[setId]) return;

    if (!images[setId] || !images[setId].file) {
      patch(setImageError, setId, `Please upload a photo before ${labels.gerund}.`);
      return;
    }
    if (!isPhotoReady(setId)) {
      patch(setImageError, setId, `Please wait for your photo to finish loading before ${labels.gerund}.`);
      return;
    }

    patch(setImageError, setId, null);
    patch(setLocationError, setId, null);

    if (navigator.permissions && navigator.permissions.query) {
      requesting.current[setId] = true;
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          requesting.current[setId] = false;
          if (result.state === 'granted') {
            proceed(setId);
          } else if (result.state === 'denied') {
            patch(
              setLocationError,
              setId,
              `Location sharing is blocked in your browser settings. To enable: update your settings, refresh, and try ${labels.gerund} again. You can still ${labels.verb} without sharing location.`
            );
            patch(setSkipLocation, setId, true);
          } else {
            requestLocationPrompt(setId);
          }
        })
        .catch(() => {
          requesting.current[setId] = false;
          requestLocationPrompt(setId);
        });
    } else {
      requestLocationPrompt(setId);
    }
  };

  const skipLocationAndProceed = (setId) => {
    patch(setSkipLocation, setId, true);
    proceed(setId, true);
  };

  return {
    isLoading,
    isBusy,
    isPhotoReady,
    isPhotoLoading,
    success,
    images,
    imageError,
    locationError,
    locationData,
    skipLocation,
    registerFileInput,
    triggerFileInput,
    handleFileSelect,
    removeImage,
    request,
    proceed,
    skipLocationAndProceed,
  };
}
